import { useState, useMemo, useEffect, useCallback } from "react";
import { Select } from "shared/components";
import type { BaseOption } from "shared/components";
import { formatPhone } from "shared/utils";
import { useAllPhoneNumbers } from "../../phone-numbers/lib/hooks";
import { useCrmContact } from "../api/crm";
import { useSendOptions, useSmsOptOuts } from "../api/hooks";
import {
  OPTED_OUT_SMS_MESSAGE,
  buildOptedOutSet,
  isPhoneOptedOut
} from "../utils/smsOptOut";
import { RoutingFieldsRow } from "./RoutingFieldsRow";
import { useComposerNarrow } from "../context/useComposerNarrow";
import { cx } from "shared/utils/classnames";
import {
  PARTICIPANT_FIELD_STYLES,
  FOOTER_PILL_STYLES,
  FOOTER_PILL_ICON_STYLES
} from "./participantFieldStyles";
import { ToRecipientsSelect } from "./ToRecipientsSelect";
import { TwoLineOptionLabel } from "./TwoLineOptionLabel";
import type { MultiRecipientControls } from "./multi-recipient";

// Bare 10-digit form so numbers compare regardless of +1/formatting.
const digitsOnly = (value: string) => {
  const digits = value.replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
};

interface SmsRoutingSelectProps {
  contactId?: string;
  conversationId?: string;
  onSelectionChange?: (
    phoneNumberId: string,
    toPhone: string,
    isToOptedOut: boolean
  ) => void;
  /** The composer sits at the bottom of /inbox, so menus open upward there. */
  menuPlacement?: "top" | "bottom" | "auto";
  /**
   * Pop-up participants block (CRM-1133 design): labeled boxed fields —
   * stacked on the floating card, side by side in the drawer.
   */
  compact?: boolean;
  multiRecipient?: MultiRecipientControls;
}

export const SmsRoutingSelect = ({
  contactId,
  conversationId,
  onSelectionChange,
  menuPlacement = "top",
  compact = false,
  multiRecipient
}: SmsRoutingSelectProps) => {
  const isNarrow = useComposerNarrow();
  const [selectedFrom, setSelectedFrom] = useState<string | null>(null);
  const [selectedTo, setSelectedTo] = useState<string | null>(null);

  const { data: contact } = useCrmContact(contactId || "");

  const { data: sendOptions } = useSendOptions(conversationId);
  const { data: optOutData } = useSmsOptOuts(conversationId || "");

  // null → no restriction (unrestricted user, or options not loaded yet).
  const allowedToPhones = useMemo(() => {
    if (!sendOptions || sendOptions.unrestricted) return null;
    return new Set(sendOptions.phones.map(digitsOnly));
  }, [sendOptions]);

  const optedOutSet = useMemo(
    () => buildOptedOutSet(optOutData?.opted_out_numbers),
    [optOutData]
  );

  const { data: phoneNumbersData, isLoading: isLoadingNumbers } =
    useAllPhoneNumbers();

  const activeNumbers = useMemo(
    () =>
      phoneNumbersData?.items.filter(
        (phoneNumber) => phoneNumber.status === "active" && phoneNumber.can_sms
      ) ?? [],
    [phoneNumbersData]
  );

  const sortedNumbers = useMemo(
    () =>
      [...activeNumbers].sort((first, second) =>
        (first.phone_number ?? "").localeCompare(second.phone_number ?? "")
      ),
    [activeNumbers]
  );

  const fromOptions: BaseOption[] = useMemo(
    () =>
      sortedNumbers.map((phoneNumber) => ({
        value: phoneNumber.communication_service_id,
        label: (
          <TwoLineOptionLabel
            title={formatPhone(phoneNumber.phone_number ?? "")}
            subtitle={phoneNumber.name}
          />
        )
      })),
    [sortedNumbers]
  );

  const toOptions: BaseOption[] = useMemo(() => {
    if (!contact) return [];
    const contactName = [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const visiblePhones = allowedToPhones
      ? contact.phones.filter((phone) => allowedToPhones.has(digitsOnly(phone)))
      : contact.phones;

    return visiblePhones.map((phone) => {
      const optedOut = isPhoneOptedOut(phone, optedOutSet);
      return {
        value: phone,
        isDisabled: optedOut,
        disabledMessage: optedOut ? OPTED_OUT_SMS_MESSAGE : undefined,
        label: (
          <TwoLineOptionLabel
            title={formatPhone(phone)}
            subtitle={contactName}
          />
        )
      };
    });
  }, [contact, allowedToPhones, optedOutSet]);

  const effectiveFrom = useMemo(() => {
    if (
      selectedFrom &&
      sortedNumbers.some(
        (phoneNumber) => phoneNumber.communication_service_id === selectedFrom
      )
    )
      return selectedFrom;
    return sortedNumbers[0]?.communication_service_id ?? "";
  }, [selectedFrom, sortedNumbers]);

  const effectiveTo = useMemo(() => {
    // Keep an explicit selection only while it is still selectable (a live
    // STOP can disable the previously-picked number).
    if (
      selectedTo &&
      toOptions.some(
        (option) => option.value === selectedTo && !option.isDisabled
      )
    )
      return selectedTo;
    // Prefer the first number that has not opted out; fall back to the first
    // number so an all-opted-out contact still shows a (blocked) value that the
    // composer explains rather than silently clearing the field.
    const firstEnabled = toOptions.find((option) => !option.isDisabled)?.value;
    const fallback = firstEnabled ?? toOptions[0]?.value;
    return typeof fallback === "string" ? fallback : "";
  }, [selectedTo, toOptions]);

  const isToOptedOut = useMemo(
    () => isPhoneOptedOut(effectiveTo, optedOutSet),
    [effectiveTo, optedOutSet]
  );

  const handleChange = useCallback(() => {
    if (effectiveFrom && effectiveTo && onSelectionChange)
      onSelectionChange(effectiveFrom, effectiveTo, isToOptedOut);
  }, [effectiveFrom, effectiveTo, isToOptedOut, onSelectionChange]);

  useEffect(() => {
    handleChange();
  }, [handleChange]);

  const hasNoFromNumbers = !isLoadingNumbers && fromOptions.length === 0;
  const hasNoToPhones = toOptions.length === 0;

  if (compact)
    return (
      <div className="flex w-full min-w-0 flex-row flex-nowrap gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-content-200">From</span>
          {hasNoFromNumbers ? (
            <span className="block text-sm font-medium text-content-300">
              No phone numbers available
            </span>
          ) : (
            <Select
              id="from-phone"
              hideOptional
              searchable={false}
              value={effectiveFrom}
              onChange={(event) => setSelectedFrom(event.target.value)}
              options={fromOptions}
              className="!min-w-0 !font-medium"
              styles={PARTICIPANT_FIELD_STYLES}
              legacyErrorMargins={false}
              isDisabled={fromOptions.length === 0}
              menuPlacement={menuPlacement}
              placeholder="Select..."
            />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-content-200">To</span>
          {hasNoToPhones ? (
            <span className="block text-sm font-medium text-content-300">
              No phone number on file
            </span>
          ) : (
            <Select
              id="to-phone"
              hideOptional
              searchable={false}
              value={effectiveTo}
              onChange={(event) => setSelectedTo(event.target.value)}
              options={toOptions}
              className="!min-w-0 !font-medium"
              styles={PARTICIPANT_FIELD_STYLES}
              legacyErrorMargins={false}
              menuPlacement={menuPlacement}
            />
          )}
        </div>
      </div>
    );

  let toField = (
    <Select
      id="to-phone"
      hideOptional
      searchable={false}
      value={effectiveTo}
      onChange={(event) => setSelectedTo(event.target.value)}
      options={toOptions}
      className="!border-0 !shadow-none !bg-transparent !text-content-1000 !font-medium !p-0"
      legacyErrorMargins={false}
      menuPlacement={menuPlacement}
    />
  );
  if (hasNoToPhones)
    toField = (
      <Select
        id="to-phone"
        hideOptional
        searchable={false}
        value=""
        onChange={() => {}}
        options={[]}
        className="!min-w-[200px] !text-content-1000 !font-medium"
        styles={FOOTER_PILL_STYLES}
        legacyErrorMargins={false}
        isDisabled
        menuPlacement={menuPlacement}
        placeholder="No phone on file"
      />
    );
  else if (multiRecipient && contactId)
    toField = (
      <ToRecipientsSelect
        channel="sms"
        contactId={Number(contactId)}
        contactName={
          [contact?.first_name, contact?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() || ""
        }
        addresses={toOptions
          .filter((option) => typeof option.value === "string")
          .map((option) => ({
            value: option.value as string,
            disabled: option.isDisabled,
            disabledMessage: option.disabledMessage
          }))}
        selectedAddress={effectiveTo}
        onSelectAddress={setSelectedTo}
        extraRecipients={multiRecipient.extraRecipients}
        onAddRecipient={multiRecipient.onAddRecipient}
        onRemoveRecipient={multiRecipient.onRemoveRecipient}
        primaryExcluded={multiRecipient.primaryExcluded}
        onPrimaryExcludedChange={multiRecipient.onPrimaryExcludedChange}
      />
    );

  return (
    <RoutingFieldsRow
      from={
        <Select
          id="from-phone"
          hideOptional
          searchable={false}
          value={effectiveFrom}
          onChange={(event) => setSelectedFrom(event.target.value)}
          options={fromOptions}
          className={cx(
            "!text-content-1000 !font-medium",
            isNarrow ? "!min-w-0" : "!min-w-[200px]"
          )}
          styles={isNarrow ? FOOTER_PILL_ICON_STYLES : FOOTER_PILL_STYLES}
          legacyErrorMargins={false}
          isDisabled={isLoadingNumbers || fromOptions.length === 0}
          menuPlacement={menuPlacement}
          placeholder={
            hasNoFromNumbers ? "No phone numbers available" : "Select..."
          }
        />
      }
      to={toField}
    />
  );
};
